/**
 * API client with automatic token refresh and error handling.
 *
 * Features:
 * - Automatic Authorization header injection
 * - Token refresh before expiry
 * - Retry with exponential backoff for 5xx errors
 * - Correlation ID tracking
 */

import { useAuthStore, getCurrentSession, getTokensFromSession } from '@ipsoftsrl/frontend-auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 1000;

interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  correlationId?: string;
}

interface ApiError {
  status: number;
  error: string;
  message: string;
  correlationId?: string;
}

/**
 * Make an authenticated API request.
 */
export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;

  // Ensure fresh token
  await ensureFreshToken();

  const { idToken } = useAuthStore.getState();
  const correlationId = crypto.randomUUID();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Correlation-Id': correlationId,
    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    ...(options.headers as Record<string, string> || {}),
  };

  const requestOptions: RequestInit = {
    ...options,
    headers,
  };

  // Retry logic for 5xx errors
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(url, requestOptions);

      if (response.status === 401) {
        // Token expired during request — try refresh once
        const refreshed = await tryRefreshToken();
        if (refreshed) {
          // Retry with new token
          const newToken = useAuthStore.getState().accessToken;
          headers.Authorization = `Bearer ${newToken}`;
          const retryResponse = await fetch(url, { ...requestOptions, headers });
          return handleResponse<T>(retryResponse, correlationId);
        }
        // Refresh failed — force logout
        useAuthStore.getState().logout();
        throw new ApiClientError(401, 'Session expired. Please log in again.', correlationId);
      }

      if (response.status >= 500 && attempt < MAX_RETRIES - 1) {
        // Retry with exponential backoff
        const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt);
        await sleep(delay);
        continue;
      }

      return handleResponse<T>(response, correlationId);
    } catch (error) {
      if (error instanceof ApiClientError) throw error;
      lastError = error as Error;
      if (attempt < MAX_RETRIES - 1) {
        const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt);
        await sleep(delay);
      }
    }
  }

  throw new ApiClientError(0, lastError?.message || 'Network error', correlationId);
}

async function handleResponse<T>(response: Response, correlationId: string): Promise<ApiResponse<T>> {
  const responseCorrelationId = response.headers.get('X-Correlation-Id') || correlationId;

  if (!response.ok) {
    let errorBody: ApiError;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = {
        status: response.status,
        error: 'UnknownError',
        message: response.statusText,
      };
    }
    throw new ApiClientError(
      response.status,
      errorBody.message || response.statusText,
      responseCorrelationId,
    );
  }

  const data = await response.json();
  return { data, status: response.status, correlationId: responseCorrelationId };
}

async function ensureFreshToken(): Promise<void> {
  // Get fresh session from Cognito (handles refresh automatically)
  try {
    const session = await getCurrentSession();
    if (session) {
      const tokens = getTokensFromSession(session);
      useAuthStore.getState().setTokens(tokens.accessToken, tokens.idToken);
    }
  } catch {
    // Session expired
  }
}

async function tryRefreshToken(): Promise<boolean> {
  try {
    const session = await getCurrentSession();
    if (session) {
      const tokens = getTokensFromSession(session);
      useAuthStore.getState().setTokens(tokens.accessToken, tokens.idToken);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Typed API error for consumers.
 */
export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly correlationId?: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// --- Convenience methods ---

export const api = {
  get: <T>(endpoint: string) => apiRequest<T>(endpoint, { method: 'GET' }),
  post: <T>(endpoint: string, body?: unknown) =>
    apiRequest<T>(endpoint, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(endpoint: string, body?: unknown) =>
    apiRequest<T>(endpoint, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(endpoint: string) => apiRequest<T>(endpoint, { method: 'DELETE' }),
};
